import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import LocationTrades from '../location/LocationTrades';
import LocationMyShop from '../location/LocationMyShop';
import LocationNotifications from '../location/LocationNotifications';
import LocationStaff from '../location/LocationStaff';
import LocationScoreboard from '../location/LocationScoreboard';
import LocationBottomNav from '../../components/LocationBottomNav';
import logo from '../../assets/logo.png';

const MyStoreDashboard = ({ onSignOut, userData }) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('trades');
  const [myLocation, setMyLocation] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        // Get user's profile id from users table
        const { data: userRow } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();

        if (!userRow) {
          setLoading(false);
          return;
        }

        // Check location_staff for this user
        const { data: staffRecord } = await supabase
          .from('location_staff')
          .select(`
            role,
            status,
            locations (
              id,
              store_name,
              address,
              city,
              state,
              phone,
              website,
              description,
              photo_url,
              photos,
              verified,
              visible_on_app,
              operating_hours,
              subscription_tier
            )
          `)
          .eq('user_id', userRow.id)
          .in('status', ['active', 'pending'])
          .single();

        if (staffRecord?.locations) {
          setMyLocation(staffRecord.locations);
          setMyRole(staffRecord.role);
        }
      } catch (err) {
        console.error('Error fetching location:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
  }, [user]);

  // Dismiss welcome banner after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowWelcome(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  const getTabTitle = () => {
    switch (activeTab) {
      case 'trades': return 'Trades';
      case 'shop': return 'My Shop';
      case 'notifications': return 'Notifications';
      case 'staff': return 'Staff';
      case 'scoreboard': return 'Scoreboard';
      default: return 'Dashboard';
    }
  };

  const renderTabContent = () => {
    if (!myLocation) return null;
    switch (activeTab) {
      case 'trades': return <LocationTrades location={myLocation} />;
      case 'shop': return <LocationMyShop location={myLocation} />;
      case 'notifications': return <LocationNotifications location={myLocation} />;
      case 'staff': return <LocationStaff location={myLocation} role={myRole} />;
      case 'scoreboard': return <LocationScoreboard location={myLocation} />;
      default: return null;
    }
  };

  const styles = {
    wrapper: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      borderBottom: `1px solid ${theme.border}`,
      backgroundColor: theme.bgCard,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    },
    headerLogo: {
      width: 32,
      height: 32,
      borderRadius: 8,
      objectFit: 'cover',
    },
    headerTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    headerSubtitle: {
      fontSize: 11,
      color: theme.textMuted,
      margin: 0,
    },
    signOutBtn: {
      background: 'none',
      border: 'none',
      color: theme.textMuted,
      fontSize: 13,
      cursor: 'pointer',
      padding: '6px 10px',
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      paddingBottom: 80,
    },
    welcomeBanner: {
      margin: 16,
      padding: 16,
      borderRadius: 12,
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      border: '1px solid rgba(34, 197, 94, 0.2)',
      textAlign: 'center',
    },
    welcomeTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: '#22c55e',
      margin: '0 0 4px',
    },
    welcomeText: {
      fontSize: 13,
      color: theme.textSecondary,
      margin: 0,
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 60,
    },
    spinner: {
      width: 32,
      height: 32,
      border: `3px solid ${theme.border}`,
      borderTopColor: theme.accent,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
    noLocationContainer: {
      padding: 40,
      textAlign: 'center',
    },
    noLocationTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: theme.text,
      margin: '0 0 8px',
    },
    noLocationText: {
      fontSize: 14,
      color: theme.textSecondary,
      lineHeight: 1.5,
      margin: 0,
    },
    tabHeader: {
      padding: '16px 16px 0',
    },
    tabTitle: {
      fontSize: 20,
      fontWeight: 700,
      color: theme.text,
      margin: 0,
    },
  };

  if (loading) {
    return (
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <img src={logo} alt="CardChase" style={styles.headerLogo} />
            <div>
              <p style={styles.headerTitle}>CardChase</p>
              <p style={styles.headerSubtitle}>Location Portal</p>
            </div>
          </div>
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  // User is approved but no location_staff record yet (not linked to a location)
  if (!myLocation) {
    const businessName = userData?.location_data?.business_name || 'Your store';
    return (
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <img src={logo} alt="CardChase" style={styles.headerLogo} />
            <div>
              <p style={styles.headerTitle}>{businessName}</p>
              <p style={styles.headerSubtitle}>Location Portal</p>
            </div>
          </div>
          <button style={styles.signOutBtn} onClick={onSignOut}>Sign Out</button>
        </div>
        <div style={styles.noLocationContainer}>
          <h3 style={styles.noLocationTitle}>You're Approved!</h3>
          <p style={styles.noLocationText}>
            Your application has been approved and your agreement is signed. Our team is finishing the setup of your location. You'll have full access to your dashboard shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <img src={logo} alt="CardChase" style={styles.headerLogo} />
          <div>
            <p style={styles.headerTitle}>{myLocation.store_name}</p>
            <p style={styles.headerSubtitle}>Location Portal</p>
          </div>
        </div>
        <button style={styles.signOutBtn} onClick={onSignOut}>Sign Out</button>
      </div>

      <div style={styles.content}>
        {showWelcome && (
          <div style={styles.welcomeBanner}>
            <p style={styles.welcomeTitle}>You're All Set!</p>
            <p style={styles.welcomeText}>Your location is live on CardChase. Manage everything from here.</p>
          </div>
        )}

        <div style={styles.tabHeader}>
          <h2 style={styles.tabTitle}>{getTabTitle()}</h2>
        </div>

        {renderTabContent()}
      </div>

      <LocationBottomNav activeTab={activeTab} onTabChange={setActiveTab} />
    </div>
  );
};

export default MyStoreDashboard;
